import { useState } from "react";

import { Popover, PopoverContent, PopoverTrigger } from "@/app/components/ui/popover";

/**
 * One piece of advice. `text` is the rule and carries the whole point on its
 * own — keep it to a single line. `items` is for the few rules that are really
 * a checklist (the fields of an education entry, the proficiency levels).
 */
export interface Tip {
	text: string;
	items?: string[];
}

export interface TipsContent {
	/** Heading for the popover. Used where one form shows several separate tips. */
	label?: string;
	/** One sentence on what the section is for and why it matters. */
	summary?: string;
	/** The rules. A plain string is a rule with no sub-points. */
	tips?: (string | Tip)[];
}

interface TipsProps {
	content: TipsContent;
}

const asTip = (tip: string | Tip): Tip => (typeof tip === "string" ? { text: tip } : tip);

/**
 * "Tips" helper popover. Opens on hover and on click; the trigger is a real
 * button, so it also opens with the keyboard (Enter/Space) and closes on Escape.
 *
 * The body is a bounded, scrollable list of short rules. It used to be a run of
 * prose paragraphs rendered from an HTML string, which on the longer sections
 * ran off the bottom of the screen with no way to reach the end.
 */
function Tips({ content }: TipsProps) {
	const [open, setOpen] = useState(false);
	const tips = (content.tips ?? []).map(asTip);

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					className="cursor-pointer border-[#7a7a7a] border-b border-dashed font-medium text-primary-700 text-xs"
					onMouseEnter={() => setOpen(true)}
					onMouseLeave={() => setOpen(false)}
				>
					Tips
				</button>
			</PopoverTrigger>
			<PopoverContent
				side="top"
				align="start"
				className="max-h-[min(60vh,26rem)] w-[21rem] max-w-[90vw] gap-2 overflow-y-auto px-3.5 py-3"
				onMouseEnter={() => setOpen(true)}
				onMouseLeave={() => setOpen(false)}
			>
				{content.label && <p className="font-semibold text-sm text-foreground">{content.label}</p>}
				{content.summary && <p className="text-muted-foreground text-sm leading-snug">{content.summary}</p>}
				{tips.length > 0 && (
					<ul className="list-disc space-y-2 pl-4 text-sm leading-snug marker:text-primary-700">
						{tips.map((tip) => (
							<li key={tip.text}>
								{tip.text}
								{tip.items && (
									<ul className="mt-1 list-[circle] space-y-1 pl-4 text-muted-foreground text-xs leading-snug">
										{tip.items.map((item) => (
											<li key={item}>{item}</li>
										))}
									</ul>
								)}
							</li>
						))}
					</ul>
				)}
			</PopoverContent>
		</Popover>
	);
}

export default Tips;
